import { Field, useFormikContext } from 'formik';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { ChecklistItem } from '../../../../../../models/forms/IAddUpdateProduct';
import { IProductFiles } from '../../../../../../models/response/IProductResponse';
import toast from 'react-hot-toast';
import { formatMessage } from '../../../../../../translations/format-message';
import FormikFormSelect from '../../../../../../shared-components/FormikFormHandlers/FormikFormSelect';
import { useStore } from '../../../../../../contexts/store-provider';
import { getTenantIdFromLocalStorage } from '../../../../../../helpers/localstorage-helper';
import { PlusIcon, UpIcon, DownIcon, RemoveIcon } from '../../../../../../shared-components/svgs/svgs';
import { FormattedMessage } from 'react-intl';

// ─── Types ───────────────────────────────────────────────────────────────────

interface HeaderItem {
  id: string;
  text: string;
  isInfo: boolean;
  isTouched: boolean;
  /** stored Id from API (0 = new) */
  apiId?: number;
  isDisabled?: boolean;
  addedAtSubmitCount?: number;
}

interface Header {
  id: string;
  title: string;
  items: HeaderItem[];
}

const DEFAULT_HEADER_TITLE = 'Default';
const FIELD_MAX_LENGTH = 256;
const hasHeaderWithoutCheckItems = (header: Header) => header.items.length === 0;
const normalizeHeaderTitle = (title: string) =>
  (title || '').trim() || DEFAULT_HEADER_TITLE;
const limitToFieldMaxLength = (value: string) =>
  String(value || '').slice(0, FIELD_MAX_LENGTH);

// ─── Helpers ─────────────────────────────────────────────────────────────────

const uid = () => Math.random().toString(36).slice(2, 10);

/** Build grouped headers from flat CheckItems array */
function flatToHeaders(checkItems: any[]): Header[] {
  if (!Array.isArray(checkItems) || checkItems.length === 0) return [];

  // preserve insertion order of header groups
  const orderMap: Map<string, Header> = new Map();

  checkItems.forEach((item) => {
    const nested = Array.isArray(item.CheckItems) ? item.CheckItems : item.CheckItemMessage;
    // Nested format (from API)
    if (nested && Array.isArray(nested)) {
      const title = (item.Title ?? '').trim();
      if (!orderMap.has(title)) {
        orderMap.set(title, { id: uid(), title: title || DEFAULT_HEADER_TITLE, items: [] });
      }
      nested.forEach((msg: any) => {
        orderMap.get(title)!.items.push({
          id: uid(),
          text: msg.CheckMessage ?? '',
          isInfo: !!msg.IsChecked,
          isTouched: !!msg.IsTouched,
          apiId: msg.Id ?? 0,
          isDisabled: msg.IsDisabled ?? false,
          addedAtSubmitCount: Number(msg.AddedAtSubmitCount || 0),
        });
      });
    } else {
      // Flat format (internal / old)
      const title = (item.HeaderTitle ?? '').trim();
      if (!orderMap.has(title)) {
        orderMap.set(title, { id: uid(), title: title || DEFAULT_HEADER_TITLE, items: [] });
      }
      orderMap.get(title)!.items.push({
        id: uid(),
        text: item.CheckMessage ?? '',
        isInfo: !!item.IsChecked,
        isTouched: !!item.IsTouched,
        apiId: item.Id ?? 0,
        isDisabled: item.IsDisabled ?? false,
        addedAtSubmitCount: Number(item.AddedAtSubmitCount || 0),
      });
    }
  });

  return Array.from(orderMap.values());
}

/** Flatten headers back to CheckItems array for Formik */
function headersToFlat(headers: Header[]): ChecklistItem[] {
  const result: ChecklistItem[] = [];
  headers.forEach((h) => {
    h.items.forEach((item) => {
      result.push({
        Id: item.apiId ?? 0,
        CheckMessage: item.text,
        IsChecked: item.isInfo,
        IsDisabled: item.isDisabled ?? false,
        IsTouched: item.isTouched ?? false,
        AddedAtSubmitCount: item.addedAtSubmitCount ?? 0,
        HeaderTitle: (h.title || '').trim(),
      });
    });
  });
  return result;
}

/** Swap two indices in an array (immutable) */
function swap<T>(arr: T[], i: number, j: number): T[] {
  const copy = [...arr];
  [copy[i], copy[j]] = [copy[j], copy[i]];
  return copy;
}

function getNextDefaultHeaderTitle(headers: Header[]): string {
  const existingTitles = new Set(
    headers.map((h) => (h.title || '').trim().toLowerCase())
  );
  const base = DEFAULT_HEADER_TITLE.toLowerCase();

  if (!existingTitles.has(base)) {
    return DEFAULT_HEADER_TITLE;
  }

  for (let i = 1; i <= 100; i++) {
    const candidate = `${DEFAULT_HEADER_TITLE}${i}`;
    if (!existingTitles.has(candidate.toLowerCase())) {
      return candidate;
    }
  }

  return `${DEFAULT_HEADER_TITLE}${Date.now()}`;
}
// ─── Component ───────────────────────────────────────────────────────────────

interface IProps {
  id: number;
  selectedProcess: any;
  selectedImage: IProductFiles[];
  selectedImageIndex: number;
}

const CheckItemsView: React.FC<IProps> = observer((props) => {
  const { selectedProcess, selectedImageIndex } = props;
  const { values, setFieldValue, submitCount } = useFormikContext<any>();
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const { checkItemTemplateStore } = useStore();
  const [getCheckItemTemplate, setGetCheckItemTemplate] = useState<any[]>([]);

  const {
    GetCheckItemTemplateByProcessIdService,
    getCheckItemTemplateByProcessIdDetail,
    GetCheckItemByTemplateIdService,
  } = checkItemTemplateStore;

  // ── Indices ──────────────────────────────────────────────────────────────

  const selectedProcessIndex = useMemo(
    () => values.Process.findIndex((p: any) => Number(p.ProcessId) === Number(selectedProcess)),
    [values?.Process, selectedProcess]
  );

  const fileIdx = useMemo(
    () =>
      values?.Process?.[selectedProcessIndex]?.fileIndices?.findIndex(
        (fi: any) => Number(fi.TabIndex) === Number(selectedImageIndex)
      ) ?? -1,
    [values?.Process, selectedProcessIndex, selectedImageIndex]
  );

  // ── Flat check items from Formik ──────────────────────────────────────────

  const flatCheckItems: ChecklistItem[] = useMemo(
    () =>
      values?.Process?.[selectedProcessIndex]?.fileIndices?.[fileIdx]?.CheckItems ?? [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [values?.Process, selectedProcessIndex, fileIdx]
  );

  // ── UI State: headers (derived from flat items on mount / external change) ─

  const [headers, setHeaders] = useState<Header[]>(() => flatToHeaders(flatCheckItems));
  const [editingHeaderId, setEditingHeaderId] = useState<string | null>(null);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const hasDuplicateHeaderTitles = useCallback((newHeaders: Header[]) => {
    const seen = new Set<string>();
    for (const header of newHeaders) {
      const normalized = normalizeHeaderTitle(header.title).toLowerCase();
      if (seen.has(normalized)) return true;
      seen.add(normalized);
    }
    return false;
  }, []);
  const syncHeaderValidationMap = useCallback(
    (newHeaders: Header[]) => {
      const processId = Number(
        values?.Process?.[selectedProcessIndex]?.ProcessId ?? selectedProcess
      );
      const tabIndex = Number(
        values?.Process?.[selectedProcessIndex]?.fileIndices?.[fileIdx]?.TabIndex ??
          selectedImageIndex
      );
      if (processId > 0 && tabIndex > 0) {
        const validationKey = `${processId}_${tabIndex}`;
        const hasEmptyHeader = newHeaders.some(hasHeaderWithoutCheckItems);
        setFieldValue(
          `CheckItemHeaderValidationMap.${validationKey}`,
          hasEmptyHeader,
          false
        );
      }
    },
    [
      values?.Process,
      selectedProcessIndex,
      selectedProcess,
      fileIdx,
      selectedImageIndex,
      setFieldValue,
    ]
  );
  const syncDuplicateHeaderValidationMap = useCallback(
    (newHeaders: Header[]) => {
      const processId = Number(
        values?.Process?.[selectedProcessIndex]?.ProcessId ?? selectedProcess
      );
      const tabIndex = Number(
        values?.Process?.[selectedProcessIndex]?.fileIndices?.[fileIdx]?.TabIndex ??
          selectedImageIndex
      );
      if (processId > 0 && tabIndex > 0) {
        const validationKey = `${processId}_${tabIndex}`;
        setFieldValue(
          `CheckItemDuplicateHeaderValidationMap.${validationKey}`,
          hasDuplicateHeaderTitles(newHeaders),
          false
        );
      }
    },
    [
      values?.Process,
      selectedProcessIndex,
      selectedProcess,
      fileIdx,
      selectedImageIndex,
      setFieldValue,
      hasDuplicateHeaderTitles,
    ]
  );
  const syncValidationMaps = useCallback(
    (newHeaders: Header[]) => {
      syncHeaderValidationMap(newHeaders);
      syncDuplicateHeaderValidationMap(newHeaders);
    },
    [syncHeaderValidationMap, syncDuplicateHeaderValidationMap]
  );

  // Sync internal headers state when flatCheckItems change from outside
  // (e.g. when a template is applied via the dropdown)
  const prevFlatRef = useRef<string>('');
  const prevTabKeyRef = useRef<string>('');

  // Always reset local header UI state when tab context changes, even when both tabs have empty [] check-items.
  useEffect(() => {
    const tabKey = `${selectedProcessIndex}_${fileIdx}`;
    if (tabKey !== prevTabKeyRef.current) {
      prevTabKeyRef.current = tabKey;
      const fromFlat = flatToHeaders(flatCheckItems);
      prevFlatRef.current = JSON.stringify(flatCheckItems);
      setHeaders(fromFlat);
      syncValidationMaps(fromFlat);
    }
  }, [
    selectedProcessIndex,
    fileIdx,
    flatCheckItems,
    syncValidationMaps,
  ]);

  useEffect(() => {
    const serialised = JSON.stringify(flatCheckItems);
    if (serialised !== prevFlatRef.current) {
      prevFlatRef.current = serialised;
      const fromFlat = flatToHeaders(flatCheckItems);
      setHeaders(fromFlat);
      syncValidationMaps(fromFlat);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flatCheckItems, syncValidationMaps]);

  // ── Write back to Formik whenever headers change ─────────────────────────

  const persistToFormik = useCallback(
    (newHeaders: Header[]) => {
      if (selectedProcessIndex < 0 || fileIdx < 0) return;
      const flat = headersToFlat(newHeaders);
      prevFlatRef.current = JSON.stringify(flat);
      setFieldValue(
        `Process.${selectedProcessIndex}.fileIndices.${fileIdx}.CheckItems`,
        flat
      );
      syncValidationMaps(newHeaders);
    },
    [selectedProcessIndex, fileIdx, setFieldValue, syncValidationMaps]
  );

  const updateHeaders = useCallback(
    (newHeaders: Header[]) => {
      setHeaders(newHeaders);
      persistToFormik(newHeaders);
    },
    [persistToFormik]
  );

  // ── Load templates dropdown ───────────────────────────────────────────────

  useEffect(() => {
    if (selectedProcess) {
      GetCheckItemTemplateByProcessIdService(
        Number(getTenantIdFromLocalStorage() ?? -1),
        Number(selectedProcess)
      );
      setFieldValue('SelectedCheckItemTemplateId', -1);
    }
  }, [selectedProcess, selectedImageIndex]);

  useEffect(() => {
    setGetCheckItemTemplate(getCheckItemTemplateByProcessIdDetail);
  }, [getCheckItemTemplateByProcessIdDetail]);

  // ── Add Header ───────────────────────────────────────────────────────────

  const handleAddHeader = () => {
    if (selectedProcessIndex < 0 || fileIdx < 0) {
      toast.error(formatMessage('select_image_checklist_items'));
      return;
    }
    const newHeader: Header = {
      id: uid(),
      title: getNextDefaultHeaderTitle(headers),
      items: [],
    };
    const newHeaders = [...headers, newHeader];
    updateHeaders(newHeaders);
  };

  // ── Header title edit ─────────────────────────────────────────────────────

  const handleHeaderTitleChange = (headerId: string, value: string) => {
    const limitedValue = limitToFieldMaxLength(value);
    const newHeaders = headers.map((h) =>
      h.id === headerId ? { ...h, title: limitedValue } : h
    );
    setHeaders(newHeaders);
    syncValidationMaps(newHeaders);
    // Don't persist on every keystroke
  };

  const handleHeaderTitleBlur = (headerId: string, value: string) => {
    const trimmed = limitToFieldMaxLength(value).trim();
    const newHeaders = headers.map((h) =>
      h.id === headerId ? { ...h, title: trimmed } : h
    );
    updateHeaders(newHeaders);
    setEditingHeaderId(null);
  };

  // ── Delete Header ─────────────────────────────────────────────────────────

  const handleDeleteHeader = (headerId: string) => {
    const newHeaders = headers.filter((h) => h.id !== headerId);
    updateHeaders(newHeaders);
    if (editingHeaderId === headerId) setEditingHeaderId(null);
  };

  // ── Move Header ───────────────────────────────────────────────────────────

  const handleMoveHeader = (headerId: string, direction: 'up' | 'down') => {
    const idx = headers.findIndex((h) => h.id === headerId);
    if (idx < 0) return;
    if (direction === 'up' && idx === 0) return;
    if (direction === 'down' && idx === headers.length - 1) return;
    const newIdx = direction === 'up' ? idx - 1 : idx + 1;
    updateHeaders(swap(headers, idx, newIdx));
  };

  // ── Add Item ──────────────────────────────────────────────────────────────

  const handleAddItem = (headerId: string) => {
    // Mark all existing items in this header as touched before adding new
    const newItem: HeaderItem = { id: uid(), text: '', isInfo: false, isTouched: false, apiId: 0 };
    const newHeaders = headers.map((h) =>
      h.id === headerId
        ? {
          ...h,
          items: [
            ...h.items.map((i) => ({ ...i, isTouched: true })),
            newItem,
          ],
        }
        : h
    );
    setHeaders(newHeaders);
    syncValidationMaps(newHeaders);
    setEditingItemId(newItem.id);
    // auto-scroll
    setTimeout(() => {
      scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
    }, 50);
  };

  // ── Item text change ──────────────────────────────────────────────────────

  const handleItemTextChange = (headerId: string, itemId: string, value: string) => {
    const limitedValue = limitToFieldMaxLength(value);
    const newHeaders = headers.map((h) =>
      h.id === headerId
        ? { ...h, items: h.items.map((i) => (i.id === itemId ? { ...i, text: limitedValue } : i)) }
        : h
    );
    updateHeaders(newHeaders);
  };

  // ── Item text blur — marks touched, persists; validation shown inline ─────

  const handleItemBlur = (headerId: string, itemId: string, value: string) => {
    const trimmed = limitToFieldMaxLength(value).trim();
    const newHeaders = headers.map((h) =>
      h.id === headerId
        ? {
          ...h,
          items: h.items.map((i) =>
            i.id === itemId ? { ...i, text: trimmed, isTouched: true } : i
          ),
        }
        : h
    );
    updateHeaders(newHeaders);
    setEditingItemId(null);
  };

  // ── IsInfo toggle ─────────────────────────────────────────────────────────

  const handleIsInfoToggle = (headerId: string, itemId: string, checked: boolean) => {
    const newHeaders = headers.map((h) =>
      h.id === headerId
        ? { ...h, items: h.items.map((i) => (i.id === itemId ? { ...i, isInfo: checked } : i)) }
        : h
    );
    updateHeaders(newHeaders);
  };

  // ── Delete Item ───────────────────────────────────────────────────────────

  const handleDeleteItem = (headerId: string, itemId: string) => {
    const newHeaders = headers.map((h) =>
      h.id === headerId ? { ...h, items: h.items.filter((i) => i.id !== itemId) } : h
    );
    updateHeaders(newHeaders);
    if (editingItemId === itemId) setEditingItemId(null);
  };

  // ── Move Item ─────────────────────────────────────────────────────────────

  const handleMoveItem = (headerId: string, itemId: string, direction: 'up' | 'down') => {
    const header = headers.find((h) => h.id === headerId);
    if (!header) return;
    const idx = header.items.findIndex((i) => i.id === itemId);
    if (idx < 0) return;
    if (direction === 'up' && idx === 0) return;
    if (direction === 'down' && idx === header.items.length - 1) return;
    const newIdx = direction === 'up' ? idx - 1 : idx + 1;
    const newItems = swap(header.items, idx, newIdx);
    const newHeaders = headers.map((h) =>
      h.id === headerId ? { ...h, items: newItems } : h
    );
    updateHeaders(newHeaders);
  };

  // ── Global sequential numbering ───────────────────────────────────────────

  let globalItemNo = 0;

  // ── Guard: no valid process/image selected ────────────────────────────────

  if (selectedProcessIndex < 0 || selectedImageIndex <= 0) {
    return (
      <div className="no-checklist check-item-scrollbar">
        <div className='text-center p-4 h-100 d-flex align-items-center justify-content-center text-muted small'>
        {formatMessage('no_checklist_items_found')}
      </div>
      </div>
    );
  }
 const isDimension =
    values?.Process?.[selectedProcessIndex]?.fileIndices?.[fileIdx]?.IsDimension;
  const hasDimensionData =
    Object.keys(
      values?.Process?.[selectedProcessIndex]?.DimensionDetails?.TableData ?? {}
    ).length > 0;
  if (isDimension && !hasDimensionData) 
    return (
      <div className="no-checklist check-item-scrollbar">
        <div className="text-center p-4 h-100 d-flex align-items-center justify-content-center text-muted small">
            {formatMessage('no_holiday_message')}
        </div>
      </div>
    );

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="check-items-headers-root">
      {/* ── Top toolbar ── */}
      <div className="d-flex gap-2 justify-content-end align-items-center checkItemTopDiv mb-1">
        {values?.Process?.[selectedProcessIndex]?.fileIndices?.[fileIdx] && (
          <>
            {/* Template dropdown */}
            <Field
              name="SelectedCheckItemTemplateId"
              as={FormikFormSelect}
              options={[
                { id: -1, value: formatMessage('please_select'), disabled: false },
                ...getCheckItemTemplate.map((item) => ({
                  value: item.CheckItemName,
                  id: item.Id,
                })),
              ]}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                if (Number(e.target.value) > 0) {
                  setFieldValue('SelectedCheckItemTemplateId', Number(e.target.value));
                  GetCheckItemByTemplateIdService(
                    Number(getTenantIdFromLocalStorage() ?? -1),
                    Number(e.target.value),
                    values,
                    setFieldValue,
                    selectedProcessIndex,
                    fileIdx
                  );
                } else {
                  setFieldValue('SelectedCheckItemTemplateId', -1);
                }
              }}
              className="d-flex align-items-center gap-2 add-checkItemList form-control"
            />

            {/* Add Header button */}
            <button
              type="button"
              className="btn btn-secondary  add-checkItemList d-flex align-items-center gap-1"
              onClick={handleAddHeader}
            >
              <PlusIcon />
              <span className="ps-1">{formatMessage('add_header')}</span>
            </button>
          </>
        )}
      </div>


      {/* ── Column labels ── */}
      {/* {headers.length > 0 && (
        <div className="check-items-col-labels d-flex align-items-center px-1 mb-1">
          <span className="check-item-no-label"></span>
          <span className="check-item-content-label flex-grow-1" />
          <span className="check-item-isinfo-label text-center" />
          <span className="check-item-actions-label" />
        </div>
      )} */}

      {/* ── Scrollable list ── */}
      <div className="check-item-scrollbar" ref={scrollRef}>
        {headers.length === 0 ? (
          <div className="text-center p-4 h-100 d-flex align-items-center justify-content-center text-muted small">
           {values?.Process?.[selectedProcessIndex]?.fileIndices?.[fileIdx] ? (
            <FormattedMessage id="no_headers_added" />
           ) : (
            <FormattedMessage id="add_image_to_proceed_with_check_items" />
           )}
          </div>
        ) : (
          headers.map((header, hIdx) => {
            const showActions = true;
            const showHeaderWithoutItemError =
              submitCount > 0 && hasHeaderWithoutCheckItems(header);
            return (
              <div key={header.id} className="check-item-header-block d-table w-100 mb-3">
                {/* ── Header row ── */}
                <div className="check-item-header-row d-table-row w-100 align-items-center gap-1">
                  {/* Row number — align to top of input */}
                  <span className="d-table-cell check-item-no text-muted text-center pt-1">&nbsp;</span>
                  {/* Title / edit */}
                  <div className="d-table-cell ">
                    {editingHeaderId === header.id ? (
                      <input
                        autoFocus
                        className={`form-control form-control-sm check-item-header-input ${showHeaderWithoutItemError ? 'is-invalid' : ''}`}
                        value={header.title}
                        placeholder={formatMessage('header_name_placeholder')}
                        maxLength={FIELD_MAX_LENGTH}
                        onChange={(e) => handleHeaderTitleChange(header.id, e.target.value)}
                        onBlur={(e) => handleHeaderTitleBlur(header.id, e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            (e.target as HTMLInputElement).blur();
                          }
                        }}
                      />
                    ) : (
                      <span
                        className={`check-item-header-title d-block show-Elipses ${showHeaderWithoutItemError ? 'is-invalid' : ''}`}
                        onClick={() => setEditingHeaderId(header.id)}
                      >
                        {(header.title || '').trim() || DEFAULT_HEADER_TITLE}
                      </span>
                    )}
                    {String(header.title || '').length >= FIELD_MAX_LENGTH && (
                      <span className="text-danger errorMessage">
                        {formatMessage('max_char_limit_256')}
                      </span>
                    )}
                    {/* {showHeaderWithoutItemError && (
                      <div className="invalid-feedback d-block">
                        {formatMessage('header_requires_check_item')}
                      </div>
                    )} */}
                  </div>

                  {/* Header action buttons */}
                  {showActions && header.title.trim() !== '' && (
                    <div className="d-table-cell check-item-header-actions action-buttons-group p-1">
                      {/* Add item */}
                      <button
                        type="button"
                        title={formatMessage('add_item')}
                        className="btn btn-icon btn-primary btn-sm"
                        onClick={() => handleAddItem(header.id)}
                      >
                        <PlusIcon />
                      </button>
                      {/* Move up */}
                      <button
                        type="button"
                        title="Move Up"
                        className="btn btn-icon btn-light-primary btn-sm"
                        disabled={hIdx === 0}
                        onClick={() => handleMoveHeader(header.id, 'up')}
                      >
                        <UpIcon />
                      </button>
                      {/* Move down */}
                      <button
                        type="button"
                        title="Move Down"
                        className="btn btn-icon btn-light-primary btn-sm"
                        disabled={hIdx === headers.length - 1}
                        onClick={() => handleMoveHeader(header.id, 'down')}
                      >
                        <DownIcon />
                      </button>
                      {/* Delete header */}
                      <button
                        type="button"
                        title="Delete Header"
                        className="btn-icon btn-light-danger btn-sm cross-header"
                        onClick={() => handleDeleteHeader(header.id)}
                      >
                        <RemoveIcon />
                      </button>
                    </div>
                  )}
                </div>

                {/* ── Check Items ── */}
                {(() => {
                  // Compute duplicate map for this header
                  const msgList = header.items.map(i => i.text.trim().toLowerCase()).filter(m => m !== '');
                  const hasDuplicatesInHeader = msgList.length !== new Set(msgList).size;

                  return header.items.map((item, iIdx) => {
                    globalItemNo++;

                    // Header-local duplicate check: same text as any other item in THIS header
                    const isDuplicate =
                      item.text.trim() !== '' &&
                      header.items.some(otherItem =>
                        otherItem.id !== item.id &&
                        otherItem.text.trim().toLowerCase() === item.text.trim().toLowerCase()
                      );

                    const isEmpty = item.isTouched && item.text.trim() === '';
                    const isInvalid = isDuplicate || isEmpty;

                    return (
                      <div key={item.id} className="check-item-row d-table-row">
                        {/* Row number — align to top of input */}
                        <span className="d-table-cell check-item-no text-muted text-center p-1 pt-2">{globalItemNo}</span>

                        {/* Text input wrapped for inline feedback */}
                        <div className="d-table-cell position-relative py-1">
                          <input
                            autoFocus={editingItemId === item.id}
                            className={`form-control form-control-sm check-item-text w-100 ${isInvalid ? 'is-invalid' : ''}`}
                            type="text"
                            value={item.text}
                            disabled={item.isDisabled}
                            placeholder={formatMessage('enter_check_content')}
                            maxLength={FIELD_MAX_LENGTH}
                            onChange={(e) => handleItemTextChange(header.id, item.id, e.target.value)}
                            onFocus={() => setEditingItemId(item.id)}
                            onBlur={(e) => handleItemBlur(header.id, item.id, e.target.value)}
                            id={`ci-${header.id}-${item.id}`}
                          />
                          {isDuplicate && (
                            <div className="invalid-feedback d-block errorMessage">
                              {formatMessage('duplicate_item_in_header')}
                            </div>
                          )}
                          {isEmpty && (
                            <div className="invalid-feedback d-block errorMessage">
                              {formatMessage('please_enter_check_content')}
                            </div>
                          )}
                          {String(item.text || '').length >= FIELD_MAX_LENGTH && (
                            <span className="text-danger errorMessage">
                              {formatMessage('max_char_limit_256')}
                            </span>
                          )}
                        </div>

                       
                        {/* Actions — align to top */}
                        {/* {item.text.trim() !== '' && ( */}
                        <div className="d-table-cell check-item-item-actions action-buttons-group p-1">
                          <input
                            type="checkbox"
                            className="form-check-input m-0"
                            checked={item.isInfo}
                            disabled={item.isDisabled}
                            onChange={(e) => handleIsInfoToggle(header.id, item.id, e.target.checked)}
                            title={formatMessage('is_info')}
                          />
                          <button
                            type="button"
                            title="Move Up"
                            className="btn btn-icon btn-light-primary btn-sm"
                            disabled={iIdx === 0}
                            onClick={() => handleMoveItem(header.id, item.id, 'up')}
                          >
                            <UpIcon />
                          </button>
                          <button
                            type="button"
                            title="Move Down"
                            className="btn btn-icon btn-light-primary btn-sm"
                            disabled={iIdx === header.items.length - 1}
                            onClick={() => handleMoveItem(header.id, item.id, 'down')}
                          >
                            <DownIcon />
                          </button>
                          <button
                            type="button"
                            title="Delete"
                            className="btn-icon btn-light-danger  btn-sm cross-header"
                            onClick={() => handleDeleteItem(header.id, item.id)}
                          >
                            <RemoveIcon />
                          </button>
                        </div>
                        {/* )} */}
                      </div>
                    );
                  });
                })()}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
});

export default CheckItemsView;
